import { useState, useEffect, useMemo, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { onboardingAPI, teamsV2API, usersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    UserPlus, Mail, Phone, GraduationCap, Loader2, CheckCircle2, XCircle,
    Clock, Calendar, Award, Filter, RotateCcw, UserX, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const BUCKETS = [
    { key: 'inscrit', label: 'Nouveaux inscrits', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
    { key: 'en_formation', label: 'En formation', color: 'bg-amber-100 text-amber-700', icon: GraduationCap },
    { key: 'formation_terminee', label: 'Formation terminée / Entretien', color: 'bg-purple-100 text-purple-700', icon: Clock },
    { key: 'valide', label: 'Validés', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    { key: 'refuse', label: 'Refusés', color: 'bg-rose-100 text-rose-700', icon: XCircle },
    { key: 'inactif', label: 'Inactifs', color: 'bg-slate-200 text-slate-600', icon: UserX },
];

const initials = (n) => (n || '').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export default function OnboardingAdmin() {
    const { isAdmin, isVP } = useAuth();
    const canManage = isAdmin();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeBucket, setActiveBucket] = useState('formation_terminee');

    // Filtres
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [minProgress, setMinProgress] = useState('');
    const [minQuiz, setMinQuiz] = useState('');
    const [responsable, setResponsable] = useState('all');

    // Données pour la validation
    const [teams, setTeams] = useState([]);
    const [vps, setVps] = useState([]);

    // Dialogs
    const [validateTarget, setValidateTarget] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [assignTarget, setAssignTarget] = useState(null);
    const [valTeam, setValTeam] = useState('');
    const [valVp, setValVp] = useState('');
    const [valPwMode, setValPwMode] = useState('keep');
    const [rejectReason, setRejectReason] = useState('');
    const [assignTeam, setAssignTeam] = useState('');
    const [assignVp, setAssignVp] = useState('');
    const [acting, setActing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (minProgress) params.min_progress = Number(minProgress);
            if (minQuiz) params.min_quiz = Number(minQuiz);
            if (responsable && responsable !== 'all') params.responsable = responsable;
            const res = await onboardingAPI.listCandidates(params);
            setCandidates(res.data.candidates || []);
        } catch (e) {
            toast.error('Erreur de chargement des candidats');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, minProgress, minQuiz, responsable]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        teamsV2API.getAll().then((r) => {
            const t = Array.isArray(r.data) ? r.data : (r.data.teams || []);
            setTeams(t);
        }).catch(() => {});
        usersAPI.getVPs().then((r) => {
            setVps(Array.isArray(r.data) ? r.data : (r.data.vps || r.data.users || []));
        }).catch(() => {});
    }, []);

    const counts = useMemo(() => {
        const c = {};
        BUCKETS.forEach((b) => { c[b.key] = 0; });
        candidates.forEach((cand) => { c[cand.effective_status] = (c[cand.effective_status] || 0) + 1; });
        return c;
    }, [candidates]);

    const visible = useMemo(
        () => candidates.filter((c) => c.effective_status === activeBucket),
        [candidates, activeBucket]
    );

    const resetFilters = () => {
        setDateFrom(''); setDateTo(''); setMinProgress(''); setMinQuiz(''); setResponsable('all');
    };

    const openValidate = (cand) => {
        setValidateTarget(cand);
        setValTeam(''); setValVp(cand.vp_id || ''); setValPwMode('keep');
    };

    const submitValidate = async () => {
        setActing(true);
        try {
            await onboardingAPI.validate(validateTarget.id, {
                team_id: valTeam || null,
                vp_id: valVp || null,
                password_mode: valPwMode,
            });
            toast.success(`${validateTarget.first_name} validé → Apporteur Actif`);
            setValidateTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setActing(false);
        }
    };

    const submitReject = async () => {
        setActing(true);
        try {
            await onboardingAPI.reject(rejectTarget.id, { reason: rejectReason });
            toast.success(`${rejectTarget.first_name} refusé`);
            setRejectTarget(null); setRejectReason('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors du refus');
        } finally {
            setActing(false);
        }
    };

    const openAssign = (cand) => {
        setAssignTarget(cand);
        setAssignTeam(''); setAssignVp(cand.vp_id || '');
    };

    const submitAssign = async () => {
        if (!assignVp) { toast.error('Sélectionnez un VP responsable'); return; }
        setActing(true);
        try {
            await onboardingAPI.assign(assignTarget.id, { team_id: assignTeam || null, vp_id: assignVp });
            toast.success(`${assignTarget.first_name} affecté à l'équipe`);
            setAssignTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de l\'affectation');
        } finally {
            setActing(false);
        }
    };

    if (!isAdmin() && !isVP()) {
        return (
            <DashboardLayout>
                <div className="text-center py-20 text-slate-500" data-testid="onboarding-forbidden">
                    Accès réservé aux administrateurs.
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="onboarding-admin-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                        <UserPlus className="w-7 h-7 text-purple-600" /> Onboarding
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {canManage
                            ? 'Gérez le parcours des candidats : formation, entretien, validation.'
                            : 'Suivez la progression des apprenants de votre équipe.'}
                    </p>
                </div>

                {/* Buckets */}
                <div className="flex flex-wrap gap-2" data-testid="onboarding-buckets">
                    {BUCKETS.map((b) => {
                        const Icon = b.icon;
                        const active = activeBucket === b.key;
                        return (
                            <button
                                key={b.key}
                                onClick={() => setActiveBucket(b.key)}
                                data-testid={`bucket-${b.key}`}
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                                    active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {b.label}
                                <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${active ? 'bg-white/20' : b.color}`}>{counts[b.key] || 0}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Filtres */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
                            <Filter className="w-4 h-4" /> Filtres
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Inscrit depuis</Label>
                                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="filter-date-from" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Inscrit jusqu'au</Label>
                                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="filter-date-to" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Progression min (%)</Label>
                                <Input type="number" min="0" max="100" value={minProgress} onChange={(e) => setMinProgress(e.target.value)} className="h-9" data-testid="filter-min-progress" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Score quiz min (%)</Label>
                                <Input type="number" min="0" max="100" value={minQuiz} onChange={(e) => setMinQuiz(e.target.value)} className="h-9" data-testid="filter-min-quiz" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Responsable (VP)</Label>
                                <Select value={responsable} onValueChange={setResponsable} disabled={!canManage}>
                                    <SelectTrigger className="h-9" data-testid="filter-responsable"><SelectValue placeholder="Tous" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        {vps.map((v) => (
                                            <SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end">
                                <Button variant="outline" onClick={resetFilters} className="h-9 gap-1.5" data-testid="reset-filters-btn">
                                    <RotateCcw className="w-4 h-4" /> Réinitialiser
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Liste */}
                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
                ) : visible.length === 0 ? (
                    <div className="text-center py-16 text-slate-400" data-testid="onboarding-empty">Aucun candidat dans cette catégorie.</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="candidate-list">
                        {visible.map((c) => (
                            <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`candidate-card-${c.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <Avatar className="h-11 w-11">
                                            <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold">
                                                {initials(`${c.first_name} ${c.last_name}`)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-slate-900 truncate">{c.first_name} {c.last_name}</h3>
                                                {c.responsable_name && <Badge variant="outline" className="text-xs">VP : {c.responsable_name}</Badge>}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                                                <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {c.email}</span>
                                                {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone}</span>}
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                                <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Inscrit le {fmtDate(c.created_at)}</span>
                                                <span className="inline-flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {c.progress_pct}% ({c.completed_modules}/{c.total_modules})</span>
                                                <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Quiz : {c.avg_quiz_score != null ? `${c.avg_quiz_score}%` : '—'}</span>
                                            </div>
                                            {/* Progress bar */}
                                            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-purple-500" style={{ width: `${c.progress_pct || 0}%` }} />
                                            </div>

                                            {/* Actions */}
                                            {['valide'].includes(c.effective_status) ? (
                                                <p className="mt-3 text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Validé le {fmtDate(c.validated_at)} → Apporteur</p>
                                            ) : c.effective_status === 'refuse' ? (
                                                <p className="mt-3 text-xs text-rose-600 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> Refusé le {fmtDate(c.rejected_at)}</p>
                                            ) : canManage ? (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    <Button size="sm" variant="outline" onClick={() => openAssign(c)} className="gap-1.5" data-testid={`assign-btn-${c.id}`}>
                                                        <Users className="w-4 h-4" /> {c.vp_id ? 'Réaffecter' : 'Assigner'}
                                                    </Button>
                                                    <Button size="sm" onClick={() => openValidate(c)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`validate-btn-${c.id}`}>
                                                        <CheckCircle2 className="w-4 h-4" /> Valider
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setRejectTarget(c)} className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1.5" data-testid={`reject-btn-${c.id}`}>
                                                        <XCircle className="w-4 h-4" /> Refuser
                                                    </Button>
                                                </div>
                                            ) : (
                                                <p className="mt-3 text-xs text-slate-400 inline-flex items-center gap-1" data-testid={`vp-readonly-${c.id}`}>
                                                    <Clock className="w-4 h-4" /> En formation — suivi
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Dialog Validation */}
            <Dialog open={!!validateTarget} onOpenChange={(o) => !o && setValidateTarget(null)}>
                <DialogContent data-testid="validate-dialog">
                    <DialogHeader>
                        <DialogTitle>Valider {validateTarget?.first_name} {validateTarget?.last_name}</DialogTitle>
                        <DialogDescription>Le candidat deviendra <strong>Apporteur Actif</strong> et l'accès CRM sera débloqué.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Équipe</Label>
                            <Select value={valTeam} onValueChange={setValTeam}>
                                <SelectTrigger data-testid="validate-team-select"><SelectValue placeholder="Sélectionner une équipe" /></SelectTrigger>
                                <SelectContent>
                                    {teams.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Responsable (VP)</Label>
                            <Select value={valVp} onValueChange={setValVp}>
                                <SelectTrigger data-testid="validate-vp-select"><SelectValue placeholder="Sélectionner un VP" /></SelectTrigger>
                                <SelectContent>
                                    {vps.map((v) => (<SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Mot de passe</Label>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="pwmode" checked={valPwMode === 'keep'} onChange={() => setValPwMode('keep')} data-testid="pw-keep" />
                                    Conserver le mot de passe actuel
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="pwmode" checked={valPwMode === 'reset'} onChange={() => setValPwMode('reset')} data-testid="pw-reset" />
                                    Forcer la définition d'un nouveau mot de passe (email envoyé)
                                </label>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setValidateTarget(null)}>Annuler</Button>
                        <Button onClick={submitValidate} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-validate-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider le candidat'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Affectation (Admin) — rattacher un apprenant en formation à une équipe + VP */}
            <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
                <DialogContent data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle>Affecter {assignTarget?.first_name} {assignTarget?.last_name}</DialogTitle>
                        <DialogDescription>
                            Rattachez ce candidat à une équipe et un VP responsable. Le VP pourra suivre sa progression pendant la formation (le rôle reste « Apprenant »).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Équipe</Label>
                            <Select value={assignTeam} onValueChange={setAssignTeam}>
                                <SelectTrigger data-testid="assign-team-select"><SelectValue placeholder="Sélectionner une équipe" /></SelectTrigger>
                                <SelectContent>
                                    {teams.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Responsable (VP)</Label>
                            <Select value={assignVp} onValueChange={setAssignVp}>
                                <SelectTrigger data-testid="assign-vp-select"><SelectValue placeholder="Sélectionner un VP" /></SelectTrigger>
                                <SelectContent>
                                    {vps.map((v) => (<SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignTarget(null)}>Annuler</Button>
                        <Button onClick={submitAssign} disabled={acting} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-assign-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Affecter'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Refus */}
            <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Refuser {rejectTarget?.first_name} {rejectTarget?.last_name} ?</DialogTitle>
                        <DialogDescription>Le compte sera désactivé. Vous pouvez préciser un motif (optionnel).</DialogDescription>
                    </DialogHeader>
                    <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motif du refus (optionnel)" data-testid="reject-reason-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectTarget(null)}>Annuler</Button>
                        <Button onClick={submitReject} disabled={acting} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer le refus'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
